import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// @mui material components
import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "../../../components/MDBox";
import MDButton from "../../../components/MDButton";
import MDTypography from "../../../components/MDTypography";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "../../../shared/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../shared/Navbars/DashboardNavbar";
import Footer from "../../../shared/Footer";

// NewProduct page components
import SectionInfo from "./components/SectionInfo";
import sectionService from "../../../services/sectionService";

// Esquema de validación con Yup
const SectionSchema = yup.object().shape({
  name: yup.string().required("El código es obligatorio").min(3, "Mínimo 3 caracteres"),
  description: yup.string().required("La descripción es obligatoria"),
  color: yup.string().required("El color es obligatorio"),
  order: yup.number().required("El orden es obligatorio")
});

const steps = ["1. Sobre la sección"];

function NewSection() {
  let navigate = useNavigate();
  const { id } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const isLastStep = activeStep === steps.length - 1;

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(SectionSchema),
    mode: "onChange",
    defaultValues: {
      code: "",
      description: "",
    },
  });

  useEffect(() => {
    const fetchSection = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        const { data } = await sectionService.getById(id);
        const section = data;

        Object.keys(section).forEach((key) => {
          const value = section[key] || "";
          setValue(key, value);
        });
      } catch (error) {
        console.error("Error cargando secciones:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSection();
  }, [id, setValue]);

  const handleNext = async () => {
    const fieldsToValidate =
      activeStep === 0
        ? ["name", "description", "color", "order"]
        : [];

    const result = await trigger(fieldsToValidate);

    if (result) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep(activeStep - 1);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (!id) {
        await sectionService.create(data)
      } else {
        await sectionService.update(id, data)
      }
      navigate("/sections");
    } catch (error) {
      console.error("Error guardando la sección:", error);
      const errorMessage = error.response?.data?.error || "Error al guardar.";
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <MDBox mt={6} mb={8} textAlign="center">
              <MDTypography variant="h3" fontWeight="bold">
                {id ? "Editar" : "Registrar nueva"} sección
              </MDTypography>
              <MDTypography variant="h5" color="secondary">
                Información de la sección
              </MDTypography>
            </MDBox>

            <Card>
              <MDBox mt={-3} mb={3} mx={2}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </MDBox>

              <MDBox p={3}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {activeStep === 0 && (
                    <SectionInfo control={control} errors={errors} />
                  )}

                  <MDBox mt={4} display="flex" justifyContent="space-between">
                    {activeStep === 0 ? (
                      <MDBox />
                    ) : (
                      <MDButton variant="gradient" color="light" onClick={handleBack}>
                        Atrás
                      </MDButton>
                    )}

                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={isLastStep ? handleSubmit(onSubmit) : handleNext}
                      disabled={isLoading}
                    >
                      {isLoading ? "Guardando..." : isLastStep ? "Guardar" : "Siguiente"}
                    </MDButton>
                  </MDBox>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NewSection;
